import React from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/utils";

export interface DataSourceCapability {
  label: string;
  isActive: boolean;
}

interface DataSourceCardProps {
  capabilities: DataSourceCapability[];
  children: React.ReactNode;
  id?: string;
  isConnected: boolean;
  isExpanded: boolean;
  logo: React.ReactNode;
  name: string;
  onToggle: () => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  capabilities,
  children,
  id,
  isConnected,
  isExpanded,
  logo,
  name,
  onToggle,
}) => {
  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800"
      id={id}
    >
      <button
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-4 p-5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/30"
        onClick={onToggle}
        type="button"
      >
        {logo}

        <div className="min-w-0 flex-1">
          <span className="text-[15px] font-medium text-gray-900 dark:text-white">
            {name}
          </span>
          <div className="mt-2 flex flex-wrap gap-2">
            {capabilities.map((capability) => (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs",
                  capability.isActive
                    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-400"
                    : "border-gray-200 bg-gray-50 text-gray-500 dark:border-slate-700 dark:bg-slate-700/40 dark:text-gray-400",
                )}
                key={capability.label}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    capability.isActive
                      ? "bg-green-500"
                      : "bg-gray-400 dark:bg-slate-500",
                  )}
                />
                {capability.label}
              </span>
            ))}
          </div>
        </div>

        <span
          className={cn(
            "hidden shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium sm:inline-block",
            isConnected
              ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-400"
              : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400",
          )}
        >
          {isConnected ? "Connected" : "Not connected"}
        </span>

        <ChevronDownIcon
          className={cn(
            "h-5 w-5 shrink-0 text-gray-400 transition-transform dark:text-gray-500",
            isExpanded && "rotate-180",
          )}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 px-5 pb-4 pt-1 dark:border-slate-700">
          {children}
        </div>
      )}
    </div>
  );
};

export default DataSourceCard;
